import {
  hasUnsavedNewAnnotationsSync,
  logViewerStudyActivityOnUnloadSync,
} from '../../../../extensions/cornerstone/src/utils/annotationSaveUtils';
import { isLoggedInUserStudyGraderSync } from './graderStudyViewOnly';

export function isOnViewerPage(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const path = window.location.pathname || '';
  if (path === '/viewer' || path.endsWith('/viewer')) {
    return true;
  }

  const params = new URLSearchParams(window.location.search);
  return params.has('StudyInstanceUIDs') || params.has('studyinstanceuids');
}

/**
 * True when the browser should show a leave/refresh confirmation on the viewer.
 * Must stay synchronous — used from beforeunload.
 */
export function shouldWarnViewerBeforeUnload(): boolean {
  if (!isOnViewerPage()) {
    return false;
  }

  if (!isLoggedInUserStudyGraderSync()) {
    return false;
  }

  if (hasUnsavedFormFieldChangesSync()) {
    return true;
  }

  return hasUnsavedNewAnnotationsSync();
}

function hasUnsavedFormFieldChangesSync(): boolean {
  const floatingFormStore = (window as { floatingFormStore?: { getState?: () => { isVisible: boolean } } })
    .floatingFormStore;
  if (!floatingFormStore?.getState?.()?.isVisible) {
    return false;
  }

  const baseline = (window as { floatingFormDataBaseline?: string | null }).floatingFormDataBaseline;
  const current = (window as { floatingFormDataCurrent?: string | null }).floatingFormDataCurrent;

  if (baseline == null || current == null) {
    return false;
  }

  return baseline !== current;
}

/**
 * Registers window.beforeunload while on the viewer. Returns a cleanup function.
 */
export function registerViewerBeforeUnloadGuard(): () => void {
  const tryLogActivityOnUnload = () => {
    if (!isOnViewerPage()) {
      return;
    }
    logViewerStudyActivityOnUnloadSync();
  };

  const onBeforeUnload = (event: BeforeUnloadEvent) => {
    tryLogActivityOnUnload();

    if (!shouldWarnViewerBeforeUnload()) {
      return;
    }

    event.preventDefault();
    // Legacy + modern browsers show a generic confirmation dialog.
    event.returnValue = '';
  };

  const onPageHide = (event: PageTransitionEvent) => {
    if (event.persisted) {
      return;
    }
    tryLogActivityOnUnload();
  };

  window.addEventListener('beforeunload', onBeforeUnload);
  window.addEventListener('pagehide', onPageHide);

  return () => {
    window.removeEventListener('beforeunload', onBeforeUnload);
    window.removeEventListener('pagehide', onPageHide);
  };
}
